// Reusable loading spinner. Two modes:
//   - inline (default): just the spinning icon, with an sr-only label, for
//     dropping next to button text.
//   - showLabel: renders the label as visible text beside the spinner, for
//     page-level "Loading…" blocks.
// Colour follows `currentColor`, so wrap it in a text-* class to tint it.

const SIZE_CLASS = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-8 w-8',
} as const;

interface SpinnerProps {
  readonly size?: keyof typeof SIZE_CLASS;
  readonly label?: string;
  readonly showLabel?: boolean;
  // When the spinner sits next to text that already announces the state (e.g.
  // a "Submitting…" button), set decorative so screen readers don't read the
  // status twice.
  readonly decorative?: boolean;
  readonly className?: string;
}

export function Spinner(props: SpinnerProps): JSX.Element {
  const {
    size = 'md',
    label = 'Loading',
    showLabel = false,
    decorative = false,
    className = '',
  } = props;
  const wrapperProps = decorative
    ? { 'aria-hidden': true as const }
    : { role: 'status', 'aria-live': 'polite' as const };
  return (
    <span {...wrapperProps} className={`inline-flex items-center gap-2 ${className}`}>
      <svg
        className={`animate-spin ${SIZE_CLASS[size]}`}
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z"
        />
      </svg>
      {decorative ? null : showLabel ? (
        <span>{label}</span>
      ) : (
        <span className="sr-only">{label}</span>
      )}
    </span>
  );
}
